import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorCode } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, api, errorMessage } from '../../../lib/api';
import { sessionQueryKey } from '../../../lib/session';
import {
  CodeInput,
  ConsentRow,
  MobileWizardFrame,
  PasswordField,
  SplitAuthCard,
  SplitAuthIntro,
  WizardButton,
  WizardField,
  passwordScore,
} from '../ui';
import type { AccountCodeResponse, AdminInvitation } from './types';
import styles from './AdminAccountFlow.module.css';

export interface AdminAccountFlowProps {
  token: string;
  invitation: AdminInvitation;
}

interface AccountValues {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
  consent: boolean;
}

type FieldKey = 'firstName' | 'lastName' | 'companyName' | 'email' | 'password' | 'consent';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Onboarding Admin 01/02 (M1): the first admin of an invited organization creates the account, confirms the address
 * with the six-digit code and lands in the wizard (`/setup/organization/brand`) with a setup session.
 */
export function AdminAccountFlow({ token, invitation }: AdminAccountFlowProps) {
  const [values, setValues] = useState<AccountValues>({
    firstName: invitation.firstName,
    lastName: invitation.lastName,
    companyName: invitation.organization.name,
    email: invitation.email,
    password: '',
    consent: false,
  });
  const [sent, setSent] = useState<AccountCodeResponse | null>(null);

  if (sent) {
    return <CodeStep token={token} sent={sent} onResent={setSent} onChangeAddress={() => setSent(null)} />;
  }
  return <AccountForm token={token} values={values} onValues={setValues} onSent={setSent} />;
}

function AccountForm({
  token,
  values,
  onValues,
  onSent,
}: {
  token: string;
  values: AccountValues;
  onValues: (values: AccountValues) => void;
  onSent: (sent: AccountCodeResponse) => void;
}) {
  const mobile = useIsMobile();
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [emailTaken, setEmailTaken] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const valid =
    values.firstName.trim() !== '' &&
    values.lastName.trim() !== '' &&
    values.companyName.trim() !== '' &&
    EMAIL_PATTERN.test(values.email.trim()) &&
    passwordScore(values.password) === 4 &&
    values.consent;

  function set<K extends keyof AccountValues>(key: K, value: AccountValues[K]) {
    onValues({ ...values, [key]: value });
    if (errors[key]) {
      const next = { ...errors };
      delete next[key];
      setErrors(next);
    }
    if (key === 'email') setEmailTaken(null);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || pending) return;
    setPending(true);
    setFailure(null);
    try {
      const result = await api<AccountCodeResponse>(
        `/public/invitations/${encodeURIComponent(token)}/account`,
        {
          method: 'POST',
          body: {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            companyName: values.companyName.trim(),
            email: values.email.trim(),
            password: values.password,
            consent: values.consent,
          },
        },
      );
      onSent(result);
    } catch (error) {
      if (error instanceof ApiError && error.code === ErrorCode.CONFLICT && error.fields['email']) {
        setEmailTaken(error.fields['email']);
      } else if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
        setErrors(error.fields);
      } else {
        setFailure(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  const emailError = emailTaken ? (
    <>
      {emailTaken} <Link to="/login">Stattdessen anmelden</Link>
    </>
  ) : (
    errors.email
  );

  const fields = (
    <>
      <WizardField
        label="Vorname"
        name="firstName"
        autoComplete="given-name"
        value={values.firstName}
        onChange={(event) => set('firstName', event.target.value)}
        error={errors.firstName}
      />
      <WizardField
        label="Nachname"
        name="lastName"
        autoComplete="family-name"
        value={values.lastName}
        onChange={(event) => set('lastName', event.target.value)}
        error={errors.lastName}
      />
      <WizardField
        label="Firmenname"
        name="companyName"
        autoComplete="organization"
        className={styles.full}
        value={values.companyName}
        onChange={(event) => set('companyName', event.target.value)}
        error={errors.companyName}
      />
      <WizardField
        label={emailTaken ? 'Geschäftliche E-Mail · bereits vergeben' : 'Geschäftliche E-Mail'}
        type="email"
        name="email"
        autoComplete="email"
        className={styles.full}
        value={values.email}
        onChange={(event) => set('email', event.target.value)}
        error={emailError}
      />
      <div className={styles.full}>
        <PasswordField
          value={values.password}
          onChange={(value) => set('password', value)}
          error={errors.password}
        />
      </div>
    </>
  );

  const consent = (
    <ConsentRow
      checked={values.consent}
      onChange={(checked) => set('consent', checked)}
      error={errors.consent}
    >
      {mobile
        ? 'Ich handle für das Unternehmen und akzeptiere die Bedingungen.'
        : 'Ich handle für das Unternehmen und akzeptiere die Auftragsverarbeitung und die Nutzungsbedingungen.'}
    </ConsentRow>
  );

  return (
    <SplitAuthCard
      width={540}
      eyebrow="DAMOKLES Suite"
      headline="Sie richten DAMOKLES für Ihr Unternehmen ein."
      mobileHeadline="DAMOKLES für Ihr Unternehmen einrichten"
      subline="Sechs Schritte: Marke, Firmendaten, Rechtstexte, Domain, Telefonie, Team. Zwei davon brauchen Ihre IT — die Anleitungen versenden wir für Sie."
    >
      <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {mobile ? (
          <p className={styles.lead}>
            Konto hier anlegen, die restlichen Schritte am Rechner fortsetzen — für Rechtstexte und Domain
            brauchen Sie Dateien und DNS-Zugang.
          </p>
        ) : (
          <SplitAuthIntro
            eyebrow="Neue Organisation"
            title="Konto anlegen"
            text="Dieses Konto wird Administrator und kann später weitere Nutzer einladen und Rollen vergeben."
          />
        )}
        <div className={mobile ? styles.mobileFields : styles.grid}>{fields}</div>
        {consent}
        {failure ? (
          <div className={styles.banner} role="alert">
            {failure}
          </div>
        ) : null}
        <WizardButton
          variant="primary"
          size={mobile ? 'mobile' : 'large'}
          type="submit"
          block
          disabled={!valid}
          loading={pending}
          className={clsx(mobile && styles.mobileSubmit)}
        >
          Organisation anlegen
        </WizardButton>
        {mobile ? null : (
          <span className={styles.footnote}>
            30 Tage testen · keine Zahlungsdaten nötig · jederzeit löschbar
          </span>
        )}
      </form>
    </SplitAuthCard>
  );
}

function useCountdown(seconds: number, restartKey: unknown): number {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const started = Date.now();
    const timer = window.setInterval(() => {
      const remaining = Math.max(0, seconds - Math.floor((Date.now() - started) / 1000));
      setLeft(remaining);
      if (remaining === 0) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [seconds, restartKey]);
  return left;
}

function countdownLabel(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function attemptsLine(remaining: number | null): string {
  const count =
    remaining === null ? '' : remaining === 1 ? 'Noch 1 Versuch. ' : `Noch ${remaining} Versuche. `;
  return `${count}Prüfen Sie, ob Sie den neuesten Code verwenden.`;
}

function CodeStep({
  token,
  sent,
  onResent,
  onChangeAddress,
}: {
  token: string;
  sent: AccountCodeResponse;
  onResent: (sent: AccountCodeResponse) => void;
  onChangeAddress: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mobile = useIsMobile();
  const [code, setCode] = useState('');
  const [wrong, setWrong] = useState<{ remaining: number | null } | null>(null);
  const [expired, setExpired] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [resendTick, setResendTick] = useState(0);
  const [cooldown, setCooldown] = useState(sent.resendInSeconds);
  const left = useCountdown(cooldown, resendTick);

  async function confirm(value: string) {
    if (pending) return;
    setPending(true);
    setNotice(null);
    try {
      await api<{ next: string }>(`/public/invitations/${encodeURIComponent(token)}/confirm`, {
        method: 'POST',
        body: { code: value },
      });
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      await navigate('/setup/organization/brand', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === ErrorCode.EMAIL_CODE_INVALID) {
        setWrong({ remaining: error.detailNumber('remaining') });
      } else if (error instanceof ApiError && error.code === ErrorCode.LINK_EXPIRED) {
        setExpired(error.message);
        setCode('');
      } else {
        setNotice(errorMessage(error));
        setCode('');
      }
    } finally {
      setPending(false);
    }
  }

  async function resend() {
    setNotice(null);
    try {
      const result = await api<AccountCodeResponse>(
        `/public/invitations/${encodeURIComponent(token)}/code/resend`,
        { method: 'POST', body: {} },
      );
      setWrong(null);
      setExpired(null);
      setCode('');
      setCooldown(result.resendInSeconds);
      setResendTick((tick) => tick + 1);
      onResent(result);
    } catch (error) {
      if (error instanceof ApiError && error.code === ErrorCode.RATE_LIMITED) {
        const seconds = error.detailNumber('resendInSeconds');
        if (seconds !== null) {
          setCooldown(seconds);
          setResendTick((tick) => tick + 1);
        }
      }
      setNotice(errorMessage(error));
    }
  }

  const failed = wrong !== null || expired !== null;
  const card = (
    <section className={clsx(styles.card, failed && styles.cardError)} aria-live="polite">
      {failed ? (
        <>
          <h1 className={styles.title}>{expired ? 'Neuer Code nötig' : 'Der Code stimmt nicht'}</h1>
          <CodeInput
            value={code}
            onChange={(value) => {
              setCode(value);
              if (wrong && value.length < 6) setWrong(null);
            }}
            onComplete={(value) => void confirm(value)}
            disabled={pending || expired !== null}
            error={expired ?? attemptsLine(wrong?.remaining ?? null)}
          />
          <div className={styles.actions}>
            <WizardButton variant="secondary" size="small" onClick={() => void resend()}>
              Neuen Code senden
            </WizardButton>
            <WizardButton variant="ghost" size="small" onClick={onChangeAddress}>
              Adresse ändern
            </WizardButton>
          </div>
        </>
      ) : (
        <>
          <h1 className={styles.title}>Wir haben Ihnen einen Code geschickt</h1>
          <p className={styles.text}>An {sent.maskedEmail} · sechs Ziffern, 15 Minuten gültig.</p>
          <CodeInput
            value={code}
            onChange={setCode}
            onComplete={(value) => void confirm(value)}
            disabled={pending}
            autoFocus
          />
          <span className={styles.resend}>
            Keine Mail erhalten?{' '}
            {left > 0 ? (
              `Erneut senden in ${countdownLabel(left)}`
            ) : (
              <button type="button" className={styles.linkButton} onClick={() => void resend()}>
                Neuen Code senden
              </button>
            )}
          </span>
        </>
      )}
      {notice ? (
        <div className={styles.banner} role="alert">
          {notice}
        </div>
      ) : null}
    </section>
  );

  if (mobile) {
    return (
      <MobileWizardFrame title="E-Mail bestätigen" onBack={onChangeAddress}>
        {card}
      </MobileWizardFrame>
    );
  }
  return <main className={styles.page}>{card}</main>;
}
